import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { addStudentNote, updateStudentNote, deleteStudentNote, getDocumentSignedUrl } from '../../services/scpApi';
import { documentCategories } from '../../data/data';
import { stripHtmlAndTrim } from '../../utils/stringHelpers';
import { scrollToView } from '../../utils/scrollToViewHelper';
import ReactQuill from 'react-quill';
import DocumentUpload from '../documentUpload/DocumentUpload';
import 'react-quill/dist/quill.snow.css';

function Notes({ notes, pupilId, onAddNote }) {
    const user = useSelector((state) => state.auth.authUser);

    const notesContRef = useRef();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(null);
    const [responseMessage, setResponseMessage] = useState(null);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [documentPreviews, setDocumentPreviews] = useState([]);
    const [selectedDocumentFile, setSelectedDocumentFile] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [formData, setFormData] = useState({
        content: '',
        nextSteps: '',
        followUp: '',
    });

    const [editFormData, setEditFormData] = useState({
        followUp: ''
    });

    const [validationErrors, setValidationErrors] = useState([]);
    const [scrollToNoteId, setScrollToNoteId] = useState(null);

    // Clear the form data when pupilId changes
    useEffect(() => {
        setFormData({
            content: '',
            nextSteps: '',
            followUp: '',
        });
        setEditFormData({
            followUp: ''
        });
        setEditingNoteId(null);
    }, [pupilId])

    // If scrollToNoteId is set, scroll to that note and then clear the state
    useEffect(() => {
        if (scrollToNoteId) {
            scrollToView(`note-${scrollToNoteId}`);
            setScrollToNoteId(null);
        }
    }, [scrollToNoteId]);

    // Handle input change for notes and next steps
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    }

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    }

    // Start editing a note
    const handleEditNote = (note) => {
        // set the document file if it exists returned as object 'document'
        setEditingNoteId(note.id);
        setEditFormData({
            content: note.note,
            nextSteps: note.next_steps,
            followUp: note.follow_up,
        });
        setSelectedDocumentFile(note.document);
        setSelectedCategory(documentCategories.find((doc) => doc.type === note?.document?.category) || null);
    }

    /**
     * 
     * @param {number} noteId 
     */
    const handleSaveNote = async (noteId, notifyTa) => {
        // If there is a selectedDocumentFile.file, the file has changed, add it to the form data
        try {
            setLoading('loading');
            setValidationErrors([]);

            // Validation to check if content or nextSteps are empty
            if (!stripHtmlAndTrim(editFormData.content) || !stripHtmlAndTrim(editFormData.nextSteps)) {
                setValidationErrors(['Both notes and next steps fields are required.']);
                return;
            }

            const response = await updateStudentNote({
                studentId: pupilId,
                noteId: noteId,
                content: editFormData.content || '',
                nextSteps: editFormData.nextSteps || '',
                followUp: stripHtmlAndTrim(editFormData.followUp) ? editFormData.followUp : null,
                documentFile: selectedDocumentFile?.file ? selectedDocumentFile.file : null,
                documentFilename: selectedDocumentFile?.filename ? selectedDocumentFile.filename : null,
                documentCategory: selectedDocumentFile?.category ? selectedDocumentFile.category : null,
                notifyTa: notifyTa || false,
            });

            // If there was an additional response message (e.g tutor email missing) show it
            setResponseMessage(response?.message || null);

            // Update notes on frontend
            onAddNote(response.pupil.notes);
            setEditingNoteId(null); // Exit edit mode
            // Clear selected document file and previews
            setSelectedDocumentFile({});
            setDocumentPreviews([]);
            setSelectedCategory(null);
        } catch (error) {
            setValidationErrors(['Failed to update the note.']);
        } finally {
            setTimeout(()=> {
                setLoading(null);
                setResponseMessage(null);
                setValidationErrors([]);
            }, 3000);
        }
    }


    /**
     * Handle delete note
     * 
     * @param {number} noteId - The id of the note to delete
     */
    const handleDeleteNote = async (noteId) => {

        // alert to confirm deletion
        const confirmDelete = window.confirm('Are you sure you want to delete this note?');

        if (!confirmDelete) {
            return;
        }

        try {
            // Delete the note from the backend
            const response = await deleteStudentNote({
                studentId: pupilId,
                noteId: noteId
            });

            console.log(response);

            onAddNote(response.pupil.notes);
        } catch (error) {
            console.log('Error deleting note:', error);
            setValidationErrors(['Failed to delete the note.']);
        }
    }


    /**
     * Add a new note to the student
     */
    const handleAddNote = async (e, notifyTA) => {
        if (e) {
            e.preventDefault();
        }
        setValidationErrors([]);

        // Validation to check if content or nextSteps are empty
        if (!stripHtmlAndTrim(formData.content) || !stripHtmlAndTrim(formData.nextSteps)) {
            setValidationErrors(['Both notes and next steps fields are required.']);
            return;
        }

        const newNote = new FormData();

        newNote.append('note', formData.content);
        newNote.append('next_steps', formData.nextSteps);
        if (stripHtmlAndTrim(formData.followUp)) newNote.append('follow_up', formData.followUp);
        newNote.append('notify_ta', notifyTA ? '1' : '0');

        if (selectedDocumentFile?.file) {
            newNote.append('document-file', selectedDocumentFile.file);
            newNote.append('document-filename', selectedDocumentFile.filename || 'Untitled');
            newNote.append('document-category', selectedDocumentFile.category || 'Uncategorized');
        }

        try {
            setLoading('loading');
            if (pupilId) {
                const response = await addStudentNote({ studentId: pupilId, notes: newNote });
                console.log('NOTE ADDED RESPONSE: ', response);

                // If there was an additional response message (e.g tutor email missing) show it
                setResponseMessage(response?.message || null);
                onAddNote(response.pupil.notes);
                setLoading('success');
                setTimeout(() => {
                    setLoading(null);
                    setResponseMessage(null);
                }, 5000);
                
                // Set the new note id to scroll to after render
                const newNoteId = response?.pupil?.notes[response?.pupil?.notes.length - 1]?.id;
                if (newNoteId) {
                    setScrollToNoteId(newNoteId);
                }

            } else {
                console.error('No pupil id provided');
                setLoading('error');
                setTimeout(() => {
                    setLoading(null);
                    setResponseMessage(null);
                }, 5000);
            }

            // Clear input fields
            setFormData({
                content: '',
                nextSteps: '',
                followUp: '',
            });
            setSelectedDocumentFile({});
            setDocumentPreviews([]);
            setSelectedCategory(null);
        } catch (error) {
            // Handle server side validation errors
            const errors = [];

            if (error.response && error.response.data && error.response.data.errors) {
                Object.keys(error.response.data.errors).forEach((key) => {
                    errors.push(...error.response.data.errors[key]);
                });
            } else {
                errors.push(error?.message ? error.message : 'An error occured while adding the note.')
            }

            setValidationErrors(errors);
            console.error('Error adding note:', error);
            setLoading(null);
        }
    }

    // Handle document download
    const handleDownloadDocument = async (documentId) => {
        try {
            const response = await getDocumentSignedUrl({documentId});
            
            if (response.url) {
                window.open(response.url, '_blank');
            } else {
                console.error('No signed URL returned for document download.');
            }
        } catch (error) {
            console.error('Error geting ssigned URL for document: ', error);
        }
    }

    const formatDate = (dateString) => {
        const options = { day: "2-digit", month: "2-digit", year: "numeric" };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    }

    // Handle link clicks within notes to use navigate for internal links
    useEffect(() => {
        const parent = notesContRef.current;

        if (!parent) {
            return;
        }

        const handleClick = (e) => {
            const link = e.target.closest("a");

            if (link && parent.contains(link)) {
                e.preventDefault();
                const url = new URL(link.href, window.location.origin);
                const path = url.pathname + url.search;

                if (path.includes("/students/feedback/")) {
                    navigate(path);
                } else {
                    // optionally allow external links
                    window.open(link.href, "_blank");
                }
            }
        };

        parent.addEventListener("click", handleClick);
        return () => parent.removeEventListener("click", handleClick);
    }, [navigate]);

    return (

        <div className="notes">

            <div className="notes-outer-cont">
                <div className="note-table-cont" ref={notesContRef}>
                    {notes && notes.length > 0 ? (
                        notes.map((note) => (
                            <div key={note.id} id={`note-${note.id}`} className="note-entry">
                                {/* Header */}
                                <div className="note-header">
                                    <div className="author-cont">
                                        <p className='note-author'>{note.created_by}</p>
                                        <p className="note-date">{formatDate(note.created_at)}</p>
                                    </div>

                                    <div className="note-actions">
                                        {editingNoteId === note.id ? (
                                            <>
                                                <button
                                                    className="update-notes-button text-button"
                                                    onClick={() => handleSaveNote(note.id)}
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    className='update-notes-button text-button'
                                                    onClick={() => handleSaveNote(note.id, true)}
                                                >
                                                    Save & Notify TA
                                                </button>
                                            </>
                                        ) : (
                                            ''
                                        )}
                                        { (user?.user_role === 'super_admin' || user?.user_role === 'school_admin') && <>
                                            <button onClick={() => handleEditNote(note)} className="text-button">Edit</button>
                                            <button onClick={() => handleDeleteNote(note.id)} className="text-button">Delete</button>
                                        </>}
                                    </div>
                                </div>

                                {/* Edit Mode */}
                                {editingNoteId === note.id ? (
                                    <div className="note-body">
                                        
                                        {validationErrors.length > 0 && (
                                            <div className="error-messages">
                                                {validationErrors.map((error, index) => (
                                                    <p key={index} className="error-text">{error}</p>
                                                ))}
                                               <span style={{fontSize: '12px', color: '#c73939'}}>Attention: If there is an email related error, the note has been added successfully. Refresh the notes page to see the changes.</span>
                                            </div>
                                        )}

                                        <div className="note-row">
                                            <div className="note-key">Notes:</div>
                                            <div className="note-value">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={editFormData.content}
                                                    onChange={(value) =>
                                                        setEditFormData((prev) => ({ ...prev, content: value }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="note-row">
                                            <div className="note-key">Next Steps:</div>
                                            <div className="note-value">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={editFormData.nextSteps}
                                                    onChange={(value) =>
                                                        setEditFormData((prev) => ({ ...prev, nextSteps: value }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="note-row">
                                            <div className="note-key">Follow Up:</div>
                                            <div className="note-value">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={editFormData.followUp}
                                                    onChange={(value) =>
                                                        setEditFormData((prev) => ({ ...prev, followUp: value }))
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <DocumentUpload
                                            documentPreviews={documentPreviews}
                                            setDocumentPreviews={setDocumentPreviews}
                                            selectedDocumentFile={selectedDocumentFile}
                                            setSelectedDocumentFile={setSelectedDocumentFile}
                                            selectedCategory={selectedCategory}
                                            setSelectedCategory={setSelectedCategory}
                                            setValidationErrors={setValidationErrors}
                                        />

                                    </div>
                                ) : (
                                    <div className="note-body">
                                        {note.note && (
                                            <div className="note-row note">
                                                <div className="note-key">Notes:</div>
                                                <div className="note-value" dangerouslySetInnerHTML={{ __html: note.note }} />
                                            </div>
                                        )}
                                        {note.next_steps && (
                                            <div className="note-row next-steps">
                                                <div className="note-key">Next Steps:</div>
                                                <div className="note-value" dangerouslySetInnerHTML={{ __html: note.next_steps }} />
                                            </div>
                                        )}
                                        {note.follow_up && (
                                            <div className="note-row follow-up">
                                                <div className="note-key">Follow Up:</div>
                                                <div className="note-value" dangerouslySetInnerHTML={{ __html: note.follow_up }} />
                                            </div>
                                        )}
                                        {note?.document?.id && (
                                            <div className="note-row document">
                                                <div className="note-key">Document:</div>
                                                <div className="note-value">
                                                    <p className="document-category">
                                                        {note?.document?.category}
                                                        <span onClick={() => handleDownloadDocument(note?.document?.id)} className="document-link">{note?.document?.filename}</span>
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="notes-table"><p>No notes available</p></div>
                    )}
                </div>

                {/* Add a success message after adding a note */}
                {loading === 'success' && (
                    <div className="success-messages">
                        <p>Note added successfully!</p>
                        {responseMessage && (
                            <p>{responseMessage}</p>
                            )}
                    </div>
                )}

                <h2 className="add-note-header">Add New Note:</h2>
                <div className="add-note-cont">
                    {user.name && (
                        <div className="username">
                            <p>{user.name}:</p>
                            <p>{formatDate(new Date())}</p>
                        </div>
                    )}

                    <form onSubmit={handleAddNote}>
                        <div className="note-input">
                            <label htmlFor="note-input">Notes</label>
                            <ReactQuill
                                theme="snow"
                                value={formData.content}
                                onChange={(value) =>
                                    setFormData((prev) => ({ ...prev, content: value }))
                                }
                            />
                        </div>

                        <div className="next-steps-input">
                            <label htmlFor="next-steps-input">Next Steps</label>
                            <ReactQuill
                                theme="snow"
                                value={formData.nextSteps}
                                onChange={(value) =>
                                    setFormData((prev) => ({ ...prev, nextSteps: value }))
                                }
                            />
                        </div>

                        <div className="follow-up-input">
                            <label htmlFor="follow-up-input">Follow Up</label>
                            <ReactQuill
                                theme="snow"
                                value={formData.followUp}
                                onChange={(value) =>
                                    setFormData((prev) => ({ ...prev, followUp: value }))
                                }
                            />
                        </div>

                        <DocumentUpload
                            documentPreviews={documentPreviews}
                            setDocumentPreviews={setDocumentPreviews}
                            selectedDocumentFile={selectedDocumentFile}
                            setSelectedDocumentFile={setSelectedDocumentFile}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                            setValidationErrors={setValidationErrors}
                        />

                    </form>
                </div>

                <div className="add-notes-btn-cont">

                    <button
                            className={`add-notes-btn button`}
                        type="submit"
                        disabled={loading === 'loading'}
                        onClick={handleAddNote}
                    >
                        {loading === 'loading' ? 'Adding ...' : 'Add / Upload'}
                    </button>

                    {/* On click it calls handleAddNote with notifyTA set to true if the user is not a super_admin*/}
                    {(user?.user_role == 'super_admin' || user?.user_role == 'school_admin') &&
                        <button
                            className={`add-notes-btn button`}
                            type="submit"
                            disabled={loading === 'loading'}
                            onClick={(e) => {
                                handleAddNote(e, true);
                            }}
                        >
                            {loading === 'loading' ? 'Adding ...' : 'Add / Upload (& notify TA)'}
                        </button>
                    }
                </div>

            </div>



            {validationErrors.length > 0 && (
                <div className="error-messages">
                    {validationErrors.map((error, index) => (
                        <p key={index} className="error-text">{error}</p>
                    ))}
                    <span style={{fontSize: '12px', color: '#c73939'}}>Attention: If there is an email related error, the note has been added successfully. Refresh the notes page to see the changes.</span>
                </div>
            )}

            {loading === 'success' && (
                <div className="success-messages">
                    <p>Note added successfully!</p>
                    {responseMessage && (
                        <p>{responseMessage}</p>
                        )}
                </div>
            )}
        </div>
    );
}

export default Notes;
